import { useSearchParams } from "react-router-dom";
import { decodeRules, encodeRules, type Rule } from "./advancedFilters";
import { VIEW_COLUMNS } from "../api/types";

/* Page, search, sort and filters live in the URL.
 *
 * docs/coding-standards.md: view state goes in the URL, so a screen is
 * linkable, reloadable and shareable, and nobody has to describe how to get
 * back to what they were looking at. That includes which record is open.
 *
 * This is shared rather than duplicated because it is not a component, it is
 * the rule about where state lives, and three screens holding three copies of
 * that rule is how they come to disagree about it. */
export const PAGE_SIZE = 50;

/* Sorting is a list, primary first. James, 2026-10-08: sorting a second
   column makes it the primary sort and keeps the first as the tiebreak, so a
   person can sort by state and then by units and get units within each
   state. Three levels is enough for anyone, and more would need a legend. */
export type Sort = { column: string; ascending: boolean };
export const MAX_SORTS = 3;

/** The next sort list after a click on `column`: the same column again flips
 *  it, a different column becomes primary and the rest move down. A count
 *  starts with the biggest first and text starts A to Z, unless `ascending`
 *  is given. */
export function pushSort(sorts: readonly Sort[], column: string,
                         opts: { ascending?: boolean; numeric?: boolean } = {}): Sort[] {
  const primary = sorts[0];
  if (primary && primary.column === column) {
    return [{ column, ascending: opts.ascending ?? !primary.ascending }, ...sorts.slice(1)];
  }
  const next: Sort = { column, ascending: opts.ascending ?? !opts.numeric };
  return [next, ...sorts.filter((s) => s.column !== column)].slice(0, MAX_SORTS);
}

/** `property_count.desc,name.asc` in the URL. */
export function encodeSorts(sorts: readonly Sort[]): string {
  return sorts.map((s) => `${s.column}.${s.ascending ? "asc" : "desc"}`).join(",");
}

/* A sort column the view does not have is dropped rather than sent.
   PostgREST answers 400 with "column company.x does not exist", and a link
   carrying a stale sort is an ordinary thing to paste, so this turns a broken
   screen into the screen's own default order. The older form, ?sort=x&desc=1,
   is still read, because links carrying it exist. */
export function decodeSorts(raw: string | null, legacyDesc: string | null,
                            fallback: Sort, view: string): Sort[] {
  if (!raw) return [fallback];
  const known = view ? VIEW_COLUMNS[view] : undefined;
  const parts = raw.split(",").map((p) => p.trim()).filter(Boolean);
  const out: Sort[] = [];
  for (const part of parts) {
    const m = /^(.+?)(?:\.(asc|desc))?$/.exec(part);
    if (!m || !m[1]) continue;
    const column = m[1];
    if (known && !known.includes(column)) continue;
    const ascending = m[2] ? m[2] === "asc"
      : legacyDesc === null ? fallback.ascending : legacyDesc !== "1";
    if (!out.some((s) => s.column === column)) out.push({ column, ascending });
  }
  return out.length ? out.slice(0, MAX_SORTS) : [fallback];
}

/** Every level of the sort on a query, primary first, nulls last. The
 *  screen adds its own tiebreak after this, so paging stays stable. */
export function applySorts<T extends { order: (c: string, o: { ascending: boolean; nullsFirst: boolean }) => T }>(
  q: T, sorts: readonly Sort[],
): T {
  let out = q;
  for (const s of sorts) out = out.order(s.column, { ascending: s.ascending, nullsFirst: false });
  return out;
}

export type ListParams = {
  page: number;
  search: string;
  /** The primary sort, for screens that read one. `sorts` has every level. */
  sort: string;
  ascending: boolean;
  sorts: Sort[];
  /** Named filters, whatever the screen defines. Absent keys are unset. */
  filters: Record<string, string>;
  /** Stacked filters on any column, built by the reader. See advancedFilters.ts. */
  advanced: Rule[];
  /** The open record, or null. */
  openRecord: string | null;
  /** A click on a column head. */
  sortBy: (column: string, opts?: { ascending?: boolean; numeric?: boolean }) => void;
  set: (next: Partial<{
    page: number;
    search: string;
    sort: string;
    ascending: boolean;
    sorts: Sort[];
    filters: Record<string, string>;
    advanced: Rule[];
    openRecord: string | null;
  }>) => void;
};

/* `defaultAscending` is false for a screen whose useful end is the top of a
   count. The Companies list opens on property_count descending, because the
   alphabetical default put three companies called "." and a power-washing firm
   on the first screen a rep sees. Nothing is hidden: a row with no properties
   sorts to the bottom rather than out of the book, which is the standing rule
   that a filter throws a row away permanently and a weight only moves it down
   the page. It also avoids a name filter, which would have binned an Arabic
   broker and a Hebrew locksmith along with the junk. */
/* `view` is the api view whose columns the advanced filters may name. A screen
   that does not pass one gets no advanced filters, which is right for a list
   with no catalogue written for it yet. */
/* `prefix` is for a table inside a record page, where several tables share
   one URL: the company page's people table reads ?p.q= and ?p.sort= and its
   properties table reads ?pr.q= and ?pr.sort=, so a link to a company's
   reachable people in operations opens with the search and the filters set,
   and the way back from a property finds them still set. Until 2026-10-08 those
   tables kept their state in memory (src/lib/useLocalListParams.ts), which
   lost it on every hop. */
export function useListParams(defaultSort: string, filterKeys: readonly string[],
                              defaultAscending = true, view = "", prefix = ""): ListParams {
  const [params, setParams] = useSearchParams();
  const fallback: Sort = { column: defaultSort, ascending: defaultAscending };
  const key = (name: string) => `${prefix}${name}`;

  const filters: Record<string, string> = {};
  for (const k of filterKeys) {
    const v = params.get(key(k));
    if (v) filters[k] = v;
  }

  const sorts = decodeSorts(params.get(key("sort")), params.get(key("desc")), fallback, view);
  const primary = sorts[0] ?? fallback;

  const writeSorts = (p: URLSearchParams, next: Sort[]) => {
    p.delete(key("desc"));
    const isDefault = next.length === 1 && next[0]?.column === fallback.column
      && next[0]?.ascending === fallback.ascending;
    if (isDefault) p.delete(key("sort")); else p.set(key("sort"), encodeSorts(next));
  };

  const set: ListParams["set"] = (next) => {
    const p = new URLSearchParams(params);
    // Changing what is being looked for returns to the first page. Staying on
    // page 9 of a different search shows an empty table that reads as broken.
    let resetPage = false;
    if (next.search !== undefined) {
      if (next.search) p.set(key("q"), next.search); else p.delete(key("q"));
      resetPage = true;
    }
    if (next.filters !== undefined) {
      for (const k of filterKeys) p.delete(key(k));
      for (const [k, v] of Object.entries(next.filters)) if (v) p.set(key(k), v);
      resetPage = true;
    }
    if (next.advanced !== undefined) {
      const encoded = encodeRules(next.advanced);
      if (encoded) p.set(key("adv"), encoded); else p.delete(key("adv"));
      resetPage = true;
    }
    if (next.sorts !== undefined) {
      writeSorts(p, next.sorts);
      resetPage = true;
    } else if (next.sort !== undefined) {
      writeSorts(p, pushSort(sorts, next.sort, next.ascending === undefined ? {} : { ascending: next.ascending }));
      resetPage = true;
    } else if (next.ascending !== undefined) {
      writeSorts(p, [{ column: primary.column, ascending: next.ascending }, ...sorts.slice(1)]);
      resetPage = true;
    }
    if (next.openRecord !== undefined) {
      if (next.openRecord) p.set(key("record"), next.openRecord); else p.delete(key("record"));
    }
    if (next.page !== undefined) {
      if (next.page > 1) p.set(key("page"), String(next.page)); else p.delete(key("page"));
    } else if (resetPage) {
      p.delete(key("page"));
    }
    setParams(p);
  };

  return {
    page: Math.max(1, Number(params.get(key("page")) ?? "1") || 1),
    search: params.get(key("q")) ?? "",
    sort: primary.column,
    ascending: primary.ascending,
    sorts,
    filters,
    advanced: decodeRules(params.get(key("adv")), view),
    openRecord: params.get(key("record")),
    sortBy: (column, opts) => set({ sorts: pushSort(sorts, column, opts) }),
    set,
  };
}
